import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImageIcon, Loader2, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { uploadsService, type UploadScope } from '@/services/platform/uploads';

interface Props {
  /** Current image URL — null/empty renders the placeholder. */
  value?: string | null;
  onChange: (url: string | null) => void;
  /** Drives the S3 key prefix on the BE. */
  scope: UploadScope;
  /** Optional UUID of the owning entity, traced into the S3 key. */
  ownerId?: string;
  /**
   * Cosmetic — controls preview aspect / shape:
   *   - 'logo'   → rectangular 16:6 (invoice header style)
   *   - 'avatar' → circular 80x80
   *   - 'photo'  → 4:3 thumbnail
   */
  kind?: 'logo' | 'avatar' | 'photo';
  /** Optional label rendered above the preview. */
  label?: string;
  /** Optional hint rendered under the controls. */
  hint?: string;
  className?: string;
}

const ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';

export function ImageUploadField({
  value,
  onChange,
  scope,
  ownerId,
  kind = 'logo',
  label,
  hint,
  className,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [hover, setHover] = useState(false);

  const upload = useMutation({
    mutationFn: (file: File) => uploadsService.uploadImage({ file, scope, ownerId }),
    onSuccess: (res) => {
      onChange(res.url);
      toast.success('Image uploaded');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    },
  });

  const onPick = () => fileRef.current?.click();

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Clear the input so re-picking the same file fires onChange again.
    e.target.value = '';
    upload.mutate(f);
  };

  const onClear = () => onChange(null);

  const previewClass = (() => {
    switch (kind) {
      case 'avatar':
        return 'h-20 w-20 rounded-full';
      case 'photo':
        return 'h-24 w-32 rounded-md';
      case 'logo':
      default:
        return 'h-20 w-48 rounded-md';
    }
  })();

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <div className="text-[12.5px] font-medium text-foreground">{label}</div>
      )}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onPick}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          className={cn(
            'group relative grid place-items-center overflow-hidden border border-dashed border-border bg-muted/30',
            'transition hover:border-primary/60 hover:bg-muted/50',
            previewClass,
          )}
          aria-label={value ? 'Replace image' : 'Upload image'}
          disabled={upload.isPending}
        >
          {upload.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : value ? (
            <>
              <img
                src={value}
                alt={label ?? 'Uploaded image'}
                className={cn(
                  'h-full w-full object-cover',
                  kind === 'logo' && 'object-contain bg-white p-1',
                )}
              />
              {hover && (
                <div className="absolute inset-0 grid place-items-center bg-foreground/60 text-background">
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium">
                    <Upload className="h-3.5 w-3.5" /> Replace
                  </span>
                </div>
              )}
            </>
          ) : (
            <ImageIcon className="h-5 w-5 text-muted-foreground/60" />
          )}
        </button>

        <div className="flex flex-col gap-1.5">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onPick}
            disabled={upload.isPending}
          >
            <Upload className="h-3.5 w-3.5" />
            {value ? 'Replace' : 'Upload'}
          </Button>
          {value && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onClear}
              disabled={upload.isPending}
              className="text-muted-foreground hover:text-destructive"
            >
              <X className="h-3.5 w-3.5" />
              Remove
            </Button>
          )}
        </div>
      </div>
      {hint && <p className="text-[11.5px] text-muted-foreground">{hint}</p>}
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        onChange={onFileChange}
        className="hidden"
      />
    </div>
  );
}
